import logoMark from './assets/logo-mark.png';
import type { ReactNode } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Loading } from './components/ui.js';
import { signOut, useSession } from './lib/auth.js';
import { AcceptInvitationPage } from './pages/AcceptInvitation.js';
import { InvoicesPage } from './pages/Invoices.js';
import { LiveSessionsPage } from './pages/LiveSessions.js';
import { JoinPage } from './pages/Join.js';
import { MembersPage } from './pages/Members.js';
import { OrganizationsPage } from './pages/Organizations.js';
import { OverviewPage } from './pages/Overview.js';
import { PlansPage } from './pages/Plans.js';
import { SettingsPage } from './pages/Settings.js';
import { SignInPage } from './pages/SignIn.js';
import { AdminEmailPage } from './pages/admin/Email.js';
import { AdminReleasesPage } from './pages/admin/Releases.js';
import { AdminZoomSdkPage } from './pages/admin/ZoomSdk.js';
import { AdminGatewaysPage } from './pages/admin/Gateways.js';
import { AdminPlansPage } from './pages/admin/Plans.js';
import { AdminTenantsPage } from './pages/admin/Tenants.js';
import { AdminAuditPage, AdminUsersPage } from './pages/admin/UsersAndAudit.js';

const isStaffRole = (role: string | null | undefined): boolean =>
  role === 'superadmin' || role === 'support';

function TopBar(): ReactNode {
  const { data: session } = useSession();

  return (
    <header className="topbar">
      <Link to="/" className="brand">
        <img src={logoMark} alt="" className="brand-mark" width={34} height={32} />
        RioPlay Live
      </Link>
      <div className="topbar-spacer" />
      {session?.user && (
        <>
          {/* Shown only to staff. The server enforces access regardless. */}
          {isStaffRole((session.user as { role?: string | null }).role) && (
            <Link to="/admin" className="tab">
              Admin
            </Link>
          )}
          <span className="topbar-user">{session.user.email}</span>
          <button
            type="button"
            className="button-ghost"
            onClick={() => {
              void signOut().then(() => {
                window.location.href = '/signin';
              });
            }}
          >
            Sign out
          </button>
        </>
      )}
    </header>
  );
}

/**
 * Auth gate.
 *
 * Purely a convenience for the browser — every endpoint enforces authentication
 * and tenant membership server-side regardless of what the SPA renders. The
 * attempted path is preserved so sign-in returns the user where they meant to go.
 */
function RequireAuth({ children }: { children: ReactNode }): ReactNode {
  const { data: session, isPending } = useSession();
  const location = useLocation();

  if (isPending) return <Loading label="Checking your session…" />;
  if (!session?.user) {
    return <Navigate to="/signin" replace state={{ from: location.pathname + location.search }} />;
  }
  return children;
}

export function App(): ReactNode {
  // Sign-in carries its own brand inside the split panel, so the top bar would
  // only repeat it.
  const onSignIn = useLocation().pathname === '/signin';

  return (
    <div className="app">
      {!onSignIn && <TopBar />}
      <main>
        <Routes>
          <Route path="/signin" element={<SignInPage />} />
          <Route
            path="/invitations/:invitationId"
            element={
              <RequireAuth>
                <AcceptInvitationPage />
              </RequireAuth>
            }
          />
          <Route
            path="/"
            element={
              <RequireAuth>
                <OrganizationsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/orgs/:orgId"
            element={
              <RequireAuth>
                <OverviewPage />
              </RequireAuth>
            }
          />
          <Route
            path="/orgs/:orgId/plans"
            element={
              <RequireAuth>
                <PlansPage />
              </RequireAuth>
            }
          />
          <Route
            path="/orgs/:orgId/live-sessions"
            element={
              <RequireAuth>
                <LiveSessionsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/orgs/:orgId/members"
            element={
              <RequireAuth>
                <MembersPage />
              </RequireAuth>
            }
          />
          <Route
            path="/orgs/:orgId/invoices"
            element={
              <RequireAuth>
                <InvoicesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/orgs/:orgId/settings"
            element={
              <RequireAuth>
                <SettingsPage />
              </RequireAuth>
            }
          />
          {/*
            Platform administration. The guard inside each page is cosmetic —
            every /api/admin/* endpoint enforces staff access server-side and
            returns 404 to anyone else.
          */}
          <Route path="/admin" element={<Navigate to="/admin/tenants" replace />} />
          {(
            [
              ['/admin/tenants', <AdminTenantsPage key="t" />],
              ['/admin/plans', <AdminPlansPage key="p" />],
              ['/admin/gateways', <AdminGatewaysPage key="g" />],
              ['/admin/email', <AdminEmailPage key="e" />],
              ['/admin/releases', <AdminReleasesPage key="r" />],
              ['/admin/zoom', <AdminZoomSdkPage key="z" />],
              ['/admin/users', <AdminUsersPage key="u" />],
              ['/admin/audit', <AdminAuditPage key="a" />],
            ] as const
          ).map(([path, element]) => (
            <Route key={path} path={path} element={<RequireAuth>{element}</RequireAuth>} />
          ))}

          {/*
            Redeeming a join code. Two paths on purpose: a link a publisher
            pasted into a chat carries the code, and a learner reading one off a
            whiteboard types it. Both sit behind RequireAuth, which preserves the
            destination through sign-in — so signing up mid-redemption returns
            here with the code intact rather than dropping it.
          */}
          <Route
            path="/join"
            element={
              <RequireAuth>
                <JoinPage />
              </RequireAuth>
            }
          />
          <Route
            path="/join/:code"
            element={
              <RequireAuth>
                <JoinPage />
              </RequireAuth>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
